/**
 * Dave Deal Monitoring cards (AGS-949/950) — payload → text
 *
 * Pure helpers, no React: what each Dave card says, its label and its "Why?"
 * lines, plus the deal line every card ends with. Texts follow the PM's card
 * catalogue (Cards.dc.html); the cards it has no mock for use our own copy,
 * approved by Juan (2026-11-03 and 2026-11-05).
 *
 * Money: deal prices (deal line, flag prices, price changes) are in the
 * marketplace's currency; unit economics (profit per unit, fixed fee) are USD.
 *
 * V1 cards are text only: Accept new price / Keep deal / Cancel deal come in
 * V1.1.
 */

import {
  AGENT_INFO,
  isDaveCancelledDecision,
  isDaveDoneDecision,
  isDaveMonitorDecision,
  isDaveRegisteredDecision,
  isDaveRegistrationFailedDecision,
} from "../../../constants";
import {
  SELLER_CENTRAL_CANCEL_NOTE,
  buildDaveRegisteredDoneMessage,
  getDaveRegisteredDetailLines,
  isDaveRegistrationFailed,
} from "../DaveRegisteredDone";

export const LABEL_DECISION = "NEEDS YOUR DECISION";
export const LABEL_ALERT = "ALERT";
export const LABEL_FYI = "FYI";
export const LABEL_DONE = "DONE FOR YOU";
export const LABEL_IN_PROGRESS = "IN PROGRESS";

// The superior profitability flow's name, shown on Dave-registered deals
const SUPERIOR_PROFITABILITY = "Superior Profitability:";

const LIGHTNING_DEAL = "LIGHTNING_DEAL";
const REGISTERED_BY_DAVE = "dave";
const MINUS = "−";
const HOUR_MS = 60 * 60 * 1000;
const NO_CANCEL_WINDOW_HOURS = 25;

const REGISTRATION_FAILED_TEXT =
  "Dave couldn't register this deal. Please register it manually in Seller Central.";

const NO_CANCEL_WHY =
  "The deal starts in less than 25 hours. Amazon may penalize cancellations this close to the start, so cancelling isn't an option. Matching Amazon's max deal price keeps the deal qualified.";

// --- Formatting --------------------------------------------------------------

const MARKETPLACE_CURRENCY = {
  us: "USD",
  ca: "CAD",
  mx: "MXN",
  uk: "GBP",
  de: "EUR",
  fr: "EUR",
  it: "EUR",
  es: "EUR",
  nl: "EUR",
  be: "EUR",
  ie: "EUR",
  se: "SEK",
  pl: "PLN",
  tr: "TRY",
};

const isNumber = (value) =>
  value !== null && value !== undefined && !Number.isNaN(Number(value));

const formatMoney = (value, currency = "USD") =>
  isNumber(value)
    ? new Intl.NumberFormat("en-US", { style: "currency", currency }).format(
        Number(value)
      )
    : null;

const currencyOf = (marketplace) =>
  MARKETPLACE_CURRENCY[(marketplace || "us").toLowerCase()] || "USD";

// Deal prices, in the marketplace's currency
const price = (value, marketplace) =>
  formatMoney(value, currencyOf(marketplace));

// Unit economics, always USD; signed ("−$0.06", "+$0.37")
const signedUsd = (value) =>
  isNumber(value)
    ? `${Number(value) < 0 ? MINUS : "+"}${formatMoney(Math.abs(value))}`
    : null;

// 0.62 → "+62%"
const formatLift = (lift) =>
  isNumber(lift)
    ? `${Number(lift) < 0 ? MINUS : "+"}${Math.round(Math.abs(lift) * 100)}%`
    : null;

// 2.0 → "2", 2.46 → "2.5"
const formatDays = (days) => `${Math.round(Number(days) * 10) / 10}`;

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// "2026-11-08" → { month: "Nov", day: 8 }. Read from the string: the deal's
// dates are Pacific calendar days, new Date() would shift them.
const parseDate = (isoDate) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate || "");
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

// "Nov 3–9", "Oct 30–Nov 5", "Nov 3" (no end, or a one-day deal)
export const formatDealDates = (startDate, endDate) => {
  const start = parseDate(startDate);
  if (!start) return null;
  const startText = `${MONTHS[start.month - 1]} ${start.day}`;
  const end = parseDate(endDate);
  if (!end || endDate === startDate) return startText;
  const endText =
    end.month === start.month
      ? `${end.day}`
      : `${MONTHS[end.month - 1]} ${end.day}`;
  return `${startText}–${endText}`;
};

// Midnight Pacific of a "YYYY-MM-DD" date, in epoch ms: a deal starts then
const pacificMidnight = (isoDate) => {
  const date = parseDate(isoDate);
  if (!date) return null;
  const eightUtc = Date.UTC(date.year, date.month - 1, date.day, 8);
  // 08:00 UTC is 00:00 PST or 01:00 PDT
  const pacificHour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Los_Angeles",
      hour: "numeric",
      hourCycle: "h23",
    }).format(new Date(eightUtc))
  );
  return eightUtc - pacificHour * HOUR_MS;
};

// A backend UTC timestamp, in epoch ms. Naive ones (no offset) are UTC too:
// Date.parse would read them as local time.
const parseUtc = (iso) => {
  if (!iso) return null;
  const ms = Date.parse(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(ms) ? null : ms;
};

// When the deal starts, in epoch ms: start_utc, else its Pacific start date
const dealStart = (daveData) => {
  const decision = daveData?.decision || {};
  return (
    parseUtc(decision.start_utc) ??
    pacificMidnight(decision.deal_line?.start_date)
  );
};

// "3 days", "20 hours", or null once it started
export const formatTimeToStart = (startMs, now = Date.now()) => {
  if (startMs == null) return null;
  const hours = (startMs - now) / HOUR_MS;
  if (hours <= 0) return null;
  if (hours < 24) return plural(Math.max(1, Math.floor(hours)), "hour");
  return plural(Math.floor(hours / 24), "day");
};

// --- Deal line ---------------------------------------------------------------

/**
 * The line every Dave card ends with: deal type and dates, current → deal
 * price (% off), a Lightning Deal's committed units, and time to start.
 * null when the card has no deal line (older done cards).
 */
export const getDealLine = (
  daveData,
  { marketplace, now = Date.now() } = {}
) => {
  const line = daveData?.decision?.deal_line;
  if (!line) return null;
  const isLightning = line.deal_type === LIGHTNING_DEAL;
  const dealName = isLightning
    ? "Lightning Deal"
    : line.deal_days
    ? `${line.deal_days}-day Best Deal`
    : "Best Deal";
  const dates = formatDealDates(
    line.start_date,
    isLightning ? null : line.end_date
  );

  let when = null;
  if (line.status === "live") when = "Live";
  else if (line.status === "cancelled") when = "Cancelled";
  else if (line.status === "ended") when = "Ended";
  else {
    const toStart = formatTimeToStart(dealStart(daveData), now);
    when = toStart ? `Starts in ${toStart}` : null;
  }

  let units = null;
  if (isLightning && isNumber(line.committed_units)) {
    units = isNumber(line.min_units)
      ? `${line.committed_units} committed units (Amazon min ${line.min_units})`
      : `${line.committed_units} committed units`;
  }

  return {
    deal: dates ? `${dealName} · ${dates}` : dealName,
    currentPrice: price(line.current_price, marketplace),
    dealPrice: price(line.deal_price, marketplace),
    discount: isNumber(line.discount_pct)
      ? `${MINUS}${Math.round(Math.abs(line.discount_pct))}%`
      : null,
    units,
    when,
  };
};

// --- Monitor cards (issue / reminder) ---------------------------------------

const isDaveRegistered = (daveData) =>
  daveData?.decision?.registered_by === REGISTERED_BY_DAVE;

/**
 * The flags a monitor card shows, most severe first. A flag the fixer
 * resolves on its own (raising to max) shows once done, on a done card; a
 * price above max shows even then (the deal won't qualify until fixed),
 * except on a Dave-registered deal: Dave re-checks it at the max himself and
 * reports the outcome (a price change or a cancelled deal).
 */
const getShownFlags = (daveData) =>
  (daveData?.decision?.flags || []).filter((flag) => {
    if (flag.code === "above_max") return !isDaveRegistered(daveData);
    return !flag.automatic;
  });

// An issue card left with no flag to show: nothing to render
export const isHiddenDaveCard = (daveData) =>
  isDaveMonitorDecision(daveData) &&
  daveData.decision.card_kind === "issue" &&
  getShownFlags(daveData).length === 0;

// "At $25.49 the deal passes Dave's superior profitability gate: …"
const gateLine = (gate, atPrice) => {
  if (!gate || !isNumber(gate.needed_lift)) return null;
  const subject = atPrice ? `At ${atPrice} the deal` : "The deal";
  const lift = formatLift(gate.needed_lift);
  const max = formatLift(gate.max_needed_lift);
  return gate.passed
    ? `${subject} passes Dave's superior profitability gate: it needs ${lift} more sales to break even, against the ${max} gate.`
    : `${subject} doesn't pass Dave's superior profitability gate: it needs ${lift} more sales to break even, above the ${max} gate.`;
};

// "Why?" of a unit economics flag: only what the flag carries (the full fee
// table is V2)
const unitEconomicsWhy = (details) =>
  [
    isNumber(details.profit_at_chosen) &&
      `Profit per unit at the deal price: ${signedUsd(
        details.profit_at_chosen
      )}`,
    isNumber(details.profit_at_max) &&
      `Profit per unit at Amazon's max deal price: ${signedUsd(
        details.profit_at_max
      )}`,
    isNumber(details.fixed_fee_per_unit) &&
      `Amazon's fixed deal fee: ${formatMoney(
        details.fixed_fee_per_unit
      )} per unit at expected sales`,
    isNumber(details.profit_before_fixed_fee) &&
      `Profit per unit before that fee: ${signedUsd(
        details.profit_before_fixed_fee
      )}`,
  ].filter(Boolean);

const dealTypeName = (dealType) =>
  dealType === LIGHTNING_DEAL ? "Lightning Deals" : "Best Deals";

// "Predicted days of stock is 38, under the …"
const daysOfStockText = (details, dealType) => {
  const dos = Math.round(Number(details.days_of_supply));
  const minimum = details.minimum_at_start;
  const required = details.required;
  if (isNumber(required) && isNumber(minimum) && required > minimum) {
    return `Predicted days of stock is ${dos}, under the ${required} needed: the ${minimum}-day minimum at start plus ${plural(
      required - minimum,
      "day"
    )} until it starts.`;
  }
  return `Predicted days of stock is ${dos}, under the ${
    minimum ?? required
  }-day minimum for ${dealTypeName(dealType)}.`;
};

// One flag → { label, title, text, why?, note? }
const flagView = (flag, daveData, marketplace) => {
  const details = flag.details || {};
  const dealType = daveData.decision.deal_type;

  if (flag.code === "stockout_live") {
    const daysLeft = formatDays(details.days_left);
    const outOfStock =
      Number(details.inventory) <= 0 || Number(details.days_of_stock) === 0;
    return {
      label: LABEL_ALERT,
      title: "Stockout risk",
      text: outOfStock
        ? `Out of stock with ${daysLeft} days of deal remaining.`
        : `Stock may run out before the deal ends: ${formatDays(
            details.days_of_stock
          )} days of inventory left at the deal's sales pace, ${daysLeft} days of deal remaining.`,
    };
  }

  if (flag.code === "below_unit_economics") {
    const why = unitEconomicsWhy(details);
    const title = "Below unit economics";
    if (flag.action === "keep_running") {
      return {
        label: LABEL_DONE,
        title,
        text: "Each unit loses money at the deal price, but the deal starts in less than 25 hours, so it keeps running. Amazon may penalize cancellations this close to the start.",
        why,
      };
    }
    if (flag.action === "alert") {
      return {
        label: LABEL_ALERT,
        title,
        text: `At the deal price each unit makes ${formatMoney(
          details.profit_before_fixed_fee
        )} before Amazon's fixed deal fee (${formatMoney(
          details.fixed_fee_per_unit
        )} per unit at expected sales), so the deal loses money unless it sells ${
          isNumber(details.break_even_lift)
            ? `${formatLift(details.break_even_lift)} `
            : ""
        }more than expected.`,
        why,
      };
    }
    return {
      label: LABEL_DECISION,
      title,
      text: isNumber(details.profit_at_max)
        ? "Even at Amazon's max deal price, each unit loses money after fees."
        : "At the deal price, each unit loses money after fees.",
      why,
      note: SELLER_CENTRAL_CANCEL_NOTE,
    };
  }

  if (flag.code === "insufficient_inventory") {
    if (flag.action === "keep_running") {
      return {
        label: LABEL_DONE,
        title: "Not enough inventory",
        text: `${daysOfStockText(details, dealType).replace(
          /\.$/,
          ""
        )}, but the deal starts in less than 25 hours, so it keeps running.`,
      };
    }
    return {
      label: LABEL_DECISION,
      title: "Not enough inventory",
      text: daysOfStockText(details, dealType),
      note: SELLER_CENTRAL_CANCEL_NOTE,
    };
  }

  if (flag.code === "above_max") {
    const max = price(details.max_price, marketplace);
    const chosen = price(details.chosen_price, marketplace);
    if (flag.automatic) {
      return {
        label: LABEL_FYI,
        title: "Price above Amazon's max",
        text: `Amazon's max deal price is ${max} and your deal price is ${chosen}. The deal starts in less than 25 hours, so we'll lower the price to ${max} about 2 hours before it starts to keep it qualified.`,
      };
    }
    return {
      label: LABEL_DECISION,
      title: "Price above Amazon's max",
      text: `Amazon's max deal price is ${max} and your deal price is ${chosen}, so the deal won't qualify.`,
      // The card's gate is computed at the max: the price the deal would run at
      gate: gateLine(daveData.decision.gate, max),
    };
  }

  return null;
};

/**
 * What a Dave monitor card (issue or reminder) or a failed registration
 * shows: { label, title, prefix, lines, why, note, registeredBy }.
 * lines are full sentences, in order; prefix is "Superior Profitability:"
 * on Dave-registered deals.
 */
export const getDaveCardView = (
  daveData,
  { marketplace, now = Date.now() } = {}
) => {
  const decision = daveData?.decision || {};
  const registeredBy = isDaveRegistered(daveData)
    ? "Dave-registered"
    : decision.registered_by
    ? "Seller-registered"
    : null;

  // A deal Dave registered (#972)
  if (isDaveRegisteredDecision(daveData)) {
    const failed = isDaveRegistrationFailed(daveData);
    return {
      label: failed ? LABEL_ALERT : LABEL_DONE,
      title: "Deal registered",
      prefix: SUPERIOR_PROFITABILITY,
      lines: [`${buildDaveRegisteredDoneMessage(daveData)}.`],
      why: getDaveRegisteredDetailLines(daveData),
      note: failed ? null : SELLER_CENTRAL_CANCEL_NOTE,
      registeredBy: "Dave-registered",
    };
  }

  // A deal price change Dave made (or failed to make), or a deal he cancelled
  const done = getDaveDoneLine(daveData, { marketplace });
  if (done) {
    const prefixed = done.message.startsWith(SUPERIOR_PROFITABILITY);
    const raised =
      Number(decision.target_price) > Number(decision.previous_price);
    return {
      label: done.failed ? LABEL_ALERT : LABEL_DONE,
      title: done.cancelled
        ? "Deal cancelled"
        : done.failed
        ? "Deal price not changed"
        : raised
        ? "Price below Amazon's max"
        : "Price above Amazon's max",
      prefix: prefixed ? SUPERIOR_PROFITABILITY : null,
      lines: [
        prefixed
          ? done.message.slice(SUPERIOR_PROFITABILITY.length).trim()
          : done.message,
      ],
      why: done.why,
      note: null,
      registeredBy: done.cancelled ? "Dave-registered" : registeredBy,
    };
  }

  if (isDaveRegistrationFailedDecision(daveData)) {
    return {
      label: LABEL_IN_PROGRESS,
      title: "Registration failed",
      prefix: SUPERIOR_PROFITABILITY,
      lines: [daveData.reasoning || REGISTRATION_FAILED_TEXT],
      why: [],
      note: null,
      registeredBy: "Dave-registered",
    };
  }

  if (decision.card_kind === "reminder") {
    const toStart = formatTimeToStart(dealStart(daveData), now);
    const isLightning = decision.deal_type === LIGHTNING_DEAL;
    const subject = isLightning ? "Your Lightning Deal" : "Your deal";
    return {
      label: LABEL_FYI,
      title: isLightning ? "Lightning Deal reminder" : "Reminder",
      prefix: null,
      lines: [
        toStart
          ? `${subject} starts in ${toStart}.`
          : `${subject} is starting.`,
        gateLine(decision.gate),
      ].filter(Boolean),
      why: [],
      note: null,
      registeredBy,
    };
  }

  const views = getShownFlags(daveData)
    .map((flag) => flagView(flag, daveData, marketplace))
    .filter(Boolean);
  if (views.length === 0) return null;
  const [first] = views;
  return {
    label: first.label,
    title: first.title,
    prefix: null,
    lines: views.flatMap((view) => [view.text, view.gate].filter(Boolean)),
    why: views.flatMap((view) => view.why || []),
    note: views.map((view) => view.note).find(Boolean) || null,
    registeredBy,
  };
};

// --- Done for you lines ------------------------------------------------------

/**
 * A Dave line in "Done for you": a deal price change (made or failed) or a
 * deal Dave cancelled. { message, why, failed, cancelled }.
 */
export const getDaveDoneLine = (daveData, { marketplace } = {}) => {
  const decision = daveData?.decision || {};

  if (isDaveCancelledDecision(daveData)) {
    return {
      message: `${SUPERIOR_PROFITABILITY} ${
        daveData.reasoning || `${AGENT_INFO.dave.name} cancelled this deal.`
      }`,
      why: [],
      failed: false,
      cancelled: true,
    };
  }

  if (!isDaveDoneDecision(daveData)) return null;
  const from = price(decision.previous_price, marketplace);
  const to = price(decision.target_price, marketplace);

  if (daveData.automated_execution_status === "all_attempts_failed") {
    return {
      message: `Couldn't change the deal price to Amazon's max deal price (${from} → ${to}). The deal may not qualify; check it in Seller Central.`,
      why: [],
      failed: true,
      cancelled: false,
    };
  }

  const raised =
    Number(decision.target_price) > Number(decision.previous_price);
  if (raised) {
    return {
      message: `Raised the deal price to Amazon's max deal price — ${from} → ${to}. A lower price only gave away extra discount.`,
      why: [],
      failed: false,
      cancelled: false,
    };
  }
  if (isDaveRegistered(daveData)) {
    // Dave re-checked the deal at the new max and it still passed
    return {
      message: `${SUPERIOR_PROFITABILITY} Amazon lowered the max deal price from ${from} to ${to}. Dave re-checked the deal at that price and it still passes the superior profitability gates.`,
      why: [],
      failed: false,
      cancelled: false,
    };
  }
  const changedAt = parseUtc(decision.changed_at);
  const start = dealStart(daveData);
  const inNoCancelWindow =
    changedAt != null &&
    start != null &&
    start - changedAt < NO_CANCEL_WINDOW_HOURS * HOUR_MS;
  return {
    message: `We lowered the deal's price to match Amazon's max deal price, from ${from} to ${to}.`,
    why: inNoCancelWindow ? [NO_CANCEL_WHY] : [],
    failed: false,
    cancelled: false,
  };
};
